/**
 * BrowserBreadcrumb - Navigation trail component.
 *
 * Shows the current navigation path as clickable segments.
 * Format: Domain > Context > Model (no Workspace shown)
 */

import React from "react";
import clsx from "clsx";
import type { BreadcrumbSegment, NavigationLevel, NavigationPath } from "./types";

type Props = {
    /** Breadcrumb segments to display */
    segments: BreadcrumbSegment[];

    /** Current focused level (last segment is not clickable) */
    currentLevel: NavigationLevel;

    /** Callback when a segment is clicked */
    onNavigate: (level: NavigationLevel, path: NavigationPath) => void;

    /** Dark mode flag */
    darkMode?: boolean;
};

export default class BrowserBreadcrumb extends React.Component<Props> {
    handleSegmentClick = (segment: BreadcrumbSegment) => {
        // Don't navigate if clicking current level
        if (segment.level === this.props.currentLevel) {
            return;
        }
        this.props.onNavigate(segment.level, segment.path);
    };

    render() {
        const { segments, currentLevel } = this.props;

        if (segments.length === 0) {
            return null;
        }

        return (
            <div className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] text-slate-500 dark:text-white/50 border-b border-slate-100 dark:border-white/[0.04] overflow-x-auto">
                {segments.map((segment, index) => {
                    const isCurrent = segment.level === currentLevel;

                    return (
                        <React.Fragment key={segment.key}>
                            {index > 0 && (
                                <span className="text-slate-300 dark:text-white/20">
                                    /
                                </span>
                            )}
                            <button
                                className={clsx(
                                    "px-1 py-0.5 rounded transition-colors truncate max-w-[80px]",
                                    isCurrent
                                        ? "font-medium text-slate-700 dark:text-white/80 cursor-default"
                                        : "hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-white/80"
                                )}
                                onClick={() => this.handleSegmentClick(segment)}
                                disabled={isCurrent}
                                title={segment.label}
                            >
                                {segment.label}
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }
}
